import { isAxiosError } from "axios";
import type { UseFormSetError } from "react-hook-form";
import type {
  ApiError,
  ApiErrorResponse,
  ApiFieldError,
} from "../types/apiError.type";

/**
 * Turns anything thrown by a request into an {@link ApiError}.
 *
 * - axios error with a structured body: status plus code/message/params/fieldErrors.
 * - axios error with a string body: the string becomes the message.
 * - axios error without a response (network failure): status null and no message, so the
 *   caller can show a generic "connection" text rather than axios' English one.
 * - anything else: status null and, best effort, the error's own message.
 */
export const extractApiError = (error: unknown): ApiError => {
  if (isAxiosError(error)) {
    const response = error.response;
    if (!response) return { status: null };

    const result: ApiError = { status: response.status };
    const body: unknown = response.data;

    if (typeof body === "string") {
      if (body) result.message = body;
    } else if (body && typeof body === "object") {
      const { code, message, params, fieldErrors } = body as ApiErrorResponse;
      if (typeof code === "string") result.code = code;
      if (typeof message === "string") result.message = message;
      if (params && typeof params === "object") result.params = params;
      if (Array.isArray(fieldErrors)) result.fieldErrors = fieldErrors;
    }
    return result;
  }

  return { status: null, message: (error as Error | undefined)?.message };
};

/**
 * Shows backend field-level errors inline on a react-hook-form form: one `setError` per
 * field error, with the message produced by `translateField`.
 */
export const applyFieldErrors = (
  fieldErrors: ApiError["fieldErrors"],
  setError: UseFormSetError<any>,
  translateField: (fieldError: ApiFieldError) => string
): void => {
  fieldErrors?.forEach((fe) =>
    setError(fe.field, { message: translateField(fe) })
  );
};
